/** @deprecated Scheduled for removal. See FloorplanPersonaTool for active implementation. */

import * as THREE from 'three';
import { PointoramaPlane } from './PointoramaPlane';
import { Viewer } from '../viewer/viewer';
import { FloorplanLine } from './FloorplanLine';
import { AreaAnnotation, DistanceAnnotation } from './PointoramaAnnotation';
import { HelperLine } from './HelperLine';
import { PlaneCreator } from '../personas/tools/modules/PlaneCreator';
import { DefaultColors } from '../defines';
import { LabelText } from './LabelText';
import { SphereMarker } from './SphereMarker';

type State = {
  floorplanPlane: PointoramaPlane;
  plane: PointoramaPlane;
  planeCreator: PlaneCreator;
  preExistingPlane: PointoramaPlane | null;
  sectionLine: FloorplanLine;
};

export class FloorplanTool {
  viewer: Viewer;
  scene: THREE.Scene;
  active = false;

  closing = false;
  antiDiscoMode = false;
  refiningLastPlane = false;
  orientationLocked = false;
  switchingPlane = false;
  mouse: THREE.Vector2 = new THREE.Vector2(0, 0);
  hoveredPlane: PointoramaPlane | null = null;

  states: State[] = [];
  activeState: State | null = null;

  helperLineFloor: HelperLine = new HelperLine();
  helperLineWall: HelperLine = new HelperLine();
  polygonPreview: AreaAnnotation | null = null;
  orientationLockedLabel: LabelText;
  endingMarker: SphereMarker;
  hoveredPlaneMarker: PointoramaPlane = new PointoramaPlane();

  constructor(viewer: Viewer) {
    this.viewer = viewer;

    this.scene = this.viewer.planeScene;

    this.orientationLockedLabel = new LabelText(this.viewer, '90°');
    this.orientationLockedLabel.offset = true;

    this.endingMarker = new SphereMarker(this.viewer);
    this.hoveredPlaneMarker.setThickness(0.03);
  }

  activate() {
    this.active = true;

    this.helperLineFloor.visible = false;
    this.helperLineWall.visible = false;
    this.scene.add(this.helperLineFloor);
    this.scene.add(this.helperLineWall);
    this.scene.add(this.endingMarker);
    this.scene.add(this.orientationLockedLabel);

    this.updateRender();
  }

  deactivate() {
    this.active = false;

    this.reset();

    this.scene.remove(this.helperLineFloor);
    this.scene.remove(this.helperLineWall);
    this.scene.remove(this.endingMarker);
    this.scene.remove(this.orientationLockedLabel);

    this.hoveredPlane = null;
    this.refiningLastPlane = false;
    this.orientationLocked = false;

    this.setSwitchingPlane(false);
  }

  reset() {
    // Save the current floor plane before reset
    const currentFloorPlane = this.activeState?.floorplanPlane;

    this.removeFromScene(this.activeState);
    this.activeState = null;

    for (const state of this.states) {
      this.removeFromScene(state);
    }
    this.states = [];

    this.helperLineFloor.visible = false;
    this.helperLineWall.visible = false;
    this.endingMarker.visible = false;

    this.closing = false;

    if (this.polygonPreview) {
      this.scene.remove(this.polygonPreview);
      this.polygonPreview = null;
    }

    // Restore the floor plane if requested
    if (currentFloorPlane) {
      this.activeState = this.createNewState(currentFloorPlane);
      this.addToScene(this.activeState);
    }
  }

  createNewState(basePlane: PointoramaPlane) {
    const plane = new PointoramaPlane();
    plane.color = new THREE.Color(DefaultColors.TEMPORARY_OBJECTS);
    return {
      floorplanPlane: basePlane,
      plane,
      planeCreator: new PlaneCreator(this.viewer, plane),
      preExistingPlane: null,
      sectionLine: new FloorplanLine(),
    };
  }

  addToScene(state: State | null) {
    if (!state) return;
    this.scene.add(state.plane);
    this.scene.add(state.planeCreator);
    this.scene.add(state.sectionLine);
  }

  removeFromScene(state: State | null) {
    if (!state) return;
    this.scene.remove(state.plane);
    this.scene.remove(state.planeCreator);
    this.scene.remove(state.sectionLine);
  }

  onCtrlDown() {
    if (this.refiningLastPlane) return;
    this.refiningLastPlane = true;

    const previousState = this.states.at(-1);
    if (!previousState || previousState.preExistingPlane) {
      this.refiningLastPlane = false;
      return;
    }
    this.states.pop();

    this.removeFromScene(this.activeState);
    this.activeState = previousState;

    this.scene.add(this.activeState.plane);
    this.scene.add(this.activeState.planeCreator);
    this.activeState.sectionLine.endingCornerPoint = undefined;

    this.updateRender();
  }

  onCtrlUp() {
    if (!this.refiningLastPlane) return;
    this.refiningLastPlane = false;
    if (!this.activeState) return;

    if (this.activeState.planeCreator.creationMarkers.length < 2) this.scene.remove(this.activeState.plane);
    this.scene.remove(this.activeState.planeCreator);
    this.activeState.planeCreator.adjustActivePlane();

    this.updateSectionLine();

    this.states.push(this.activeState);
    const basePlane = this.activeState.floorplanPlane;
    this.activeState = this.createNewState(basePlane);
    this.addToScene(this.activeState);

    this.updateRender();
  }

  onShiftDown() {
    this.orientationLocked = true;
    this.updateRender();
  }

  onShiftUp() {
    this.orientationLocked = false;
    this.updateRender();
  }

  onAltDown() {
    if (this.switchingPlane) return;
    this.setSwitchingPlane(true);
    this.updateRender();
  }

  onAltUp() {
    this.setSwitchingPlane(false);
    this.updateRender();
  }

  setSwitchingPlane(switchingPlane: boolean) {
    this.switchingPlane = switchingPlane;

    if (switchingPlane) {
      this.viewer.scene.dispatchEvent({
        type: 'pointorama_hint_show_plane_selection',
      });
      this.viewer.scene.planes.forEach((plane) => {
        plane.fill(true, { color: DefaultColors.HIGHLIGHT, opacity: 0.5 });
      });
      this.hoveredPlane?.fill(true, { color: DefaultColors.HIGHLIGHT, opacity: 0.8 });
    } else {
      this.viewer.scene.dispatchEvent({ type: 'pointorama_clear_hint' });
      this.hoveredPlane?.unHighlight();

      this.viewer.scene.planes.forEach((plane) => {
        plane.fill(false);
      });
    }
  }

  onMouseMove(mouse: THREE.Vector2) {
    this.mouse.copy(mouse);
    this.updateRender();
  }

  updateRender() {
    if (!this.activeState) this.setSwitchingPlane(true);
    this.updateHoveredPlane();

    this.updateActivePlane();
    this.updateSectionLine();

    this.setDiscoMode();
    this.updateActiveStateVisibility();
    this.updateHelperLines();
    this.updateEndingMarker();
    this.updateOrientationLockedLabel();
  }

  updateHoveredPlane() {
    if (this.switchingPlane) {
      this.hoveredPlane?.fill(true, { color: DefaultColors.HIGHLIGHT, opacity: 0.5 });
    } else {
      this.hoveredPlane?.fill(false);
    }
    this.hoveredPlane?.unHighlight();
    this.hoveredPlane?.remove(this.hoveredPlaneMarker);

    if (this.refiningLastPlane || this.orientationLocked) {
      this.hoveredPlane = null;
      return;
    } else {
      this.hoveredPlane = (this.viewer.inputHandler.getHoveredPlane() as PointoramaPlane | null) || null;
    }

    if (this.hoveredPlane) {
      this.hoveredPlaneMarker.color = this.hoveredPlane.color;
      this.hoveredPlaneMarker.setRadius(this.hoveredPlane.radius);
      this.hoveredPlane.add(this.hoveredPlaneMarker);
    }

    if (this.switchingPlane) {
      this.hoveredPlane?.highlight();
      this.hoveredPlane?.fill(true, { color: DefaultColors.HIGHLIGHT, opacity: 0.8 });
    }
  }

  updateActivePlane() {
    if (!this.activeState) return;
    if (this.switchingPlane) {
      const previousState = this.states.at(-1);
      const previousPlane = previousState?.preExistingPlane || previousState?.plane;
      if (previousPlane) this.activeState.plane.defineFromOtherPlane(previousPlane);
    } else {
      this.activeState.planeCreator.adjustActivePlaneToMousePosition(this.mouse);
      if (this.orientationLocked) {
        this.snapOrientation();
      }
    }
  }

  snapOrientation() {
    if (!this.activeState) return;

    const previousState = this.states.at(-1);
    if (previousState) {
      const previousPlane = previousState.preExistingPlane || previousState.plane;
      this.activeState.plane.setRotationFromNormal(previousPlane.getNormal().cross(this.activeState.floorplanPlane.getNormal()).normalize());
      this.activeState.plane.calculateInclinationAndBearing();
    } else {
      this.activeState.plane.snapInclination();

      if (this.activeState.plane.inclination < 0.0001) return;

      if (this.activeState.floorplanPlane.inclination < 0.0001) return;

      const zAxis = new THREE.Vector3(0, 0, 1);
      const floorplanPlaneNormal = this.activeState.floorplanPlane.normal;
      const snappedNormal = new THREE.Vector3().crossVectors(zAxis, floorplanPlaneNormal).normalize();
      this.activeState.plane.setRotationFromNormal(snappedNormal);
      this.activeState.plane.calculateInclinationAndBearing();
    }
  }

  updateSectionLine() {
    if (!this.activeState) return;
    this.activeState.preExistingPlane = null;

    if (this.hoveredPlane && this.switchingPlane) {
      this.activeState.sectionLine.defineFromIntersectingPlanes(this.activeState.plane, this.hoveredPlane);
    } else if (this.hoveredPlane) {
      this.activeState.preExistingPlane = this.hoveredPlane;
      const lastSectionLineInSamePlane = this.getLastSectionLineInSamePlane();
      this.activeState.sectionLine.defineFromIntersectingExistingPlanes(this.activeState.floorplanPlane, this.hoveredPlane, lastSectionLineInSamePlane);
    } else {
      this.activeState.sectionLine.defineFromIntersectingPlanes(this.activeState.floorplanPlane, this.activeState.plane);
    }

    const previousState = this.states.at(-1);
    const previousLine = previousState?.sectionLine;
    if (previousLine) {
      const cornerPoint = this.activeState.sectionLine.findPointOfIntersectionWithOtherLine(previousLine);
      if (cornerPoint) {
        this.activeState.sectionLine.setFirstCornerPoint(cornerPoint);
        previousLine.setSecondCornerPoint(cornerPoint);
      }
      this.checkClosingCondition();
      if (this.activeState.sectionLine.visible) previousLine.updateGeometry();
    }
    this.activeState.sectionLine.updateGeometry();
  }

  getLastSectionLineInSamePlane() {
    if (!this.hoveredPlane) return null;
    if (!this.activeState) return null;

    let lastSectionLineInSamePlane: FloorplanLine | null = null;
    for (const state of this.states) {
      if ((state.preExistingPlane || state.plane) === this.hoveredPlane && state.floorplanPlane === this.activeState.floorplanPlane) {
        lastSectionLineInSamePlane = state.sectionLine;
      }
    }
    return lastSectionLineInSamePlane;
  }

  setDiscoMode() {
    if (!this.activeState) return;

    const floorPlane = this.activeState.floorplanPlane;
    const wallPlane = this.hoveredPlane || this.activeState.plane;
    const angleBetweenPlanes = wallPlane.getAngleBetweenPlanes(floorPlane);

    this.antiDiscoMode = angleBetweenPlanes < Math.PI / 18;
  }

  updateActiveStateVisibility() {
    if (!this.activeState) return;

    const activeLineHidden = this.antiDiscoMode || this.closing || (this.switchingPlane && !this.hoveredPlane) || (this.switchingPlane && !this.states.length);
    const activePlaneHidden = this.antiDiscoMode || this.closing || this.hoveredPlane || this.switchingPlane;

    this.activeState.sectionLine.visible = !activeLineHidden;
    this.activeState.planeCreator.setVisible(!activePlaneHidden);
  }

  updateHelperLines() {
    if (!this.activeState) return;
    const firstState = this.states[0] || this.activeState;

    if (firstState.sectionLine.firstPointOnLine && !firstState.sectionLine.startingCornerPoint) {
      this.helperLineFloor.setBetweenPoints([firstState.floorplanPlane.position, firstState.sectionLine.firstPointOnLine]);
    }
    if (this.activeState.sectionLine.secondPointOnLine && !this.activeState.sectionLine.endingCornerPoint) {
      if (this.switchingPlane) {
        if (this.hoveredPlane) {
          this.helperLineWall.setBetweenPoints([this.hoveredPlane.position, this.activeState.sectionLine.secondPointOnLine]);
        } else {
          this.helperLineWall.setBetweenPoints([this.activeState.sectionLine.secondPointOnLine, this.activeState.sectionLine.secondPointOnLine]);
        }
      } else {
        this.helperLineWall.setBetweenPoints([this.activeState.preExistingPlane?.position || this.activeState.plane.position, this.activeState.sectionLine.secondPointOnLine]);
      }
    }

    const helperLineWallHidden = this.closing || (this.switchingPlane && !this.states.length) || this.antiDiscoMode;
    const helperLineFloorHidden = this.closing || (this.switchingPlane && !this.states.length) || (this.antiDiscoMode && !this.states.length);

    this.helperLineWall.visible = !helperLineWallHidden;
    this.helperLineFloor.visible = !helperLineFloorHidden;
  }

  updateEndingMarker() {
    if (!this.activeState) return;
    const firstState = this.states[0] || this.activeState;

    if (!this.closing) {
      const p = firstState.sectionLine.getStart();
      this.endingMarker.position.set(p.x, p.y, p.z);
    }

    this.endingMarker.visible = this.states.length > 2;
  }

  updateOrientationLockedLabel() {
    if (!this.activeState || !this.orientationLocked || this.states.length === 0 || this.hoveredPlane || this.antiDiscoMode || this.closing) {
      this.orientationLockedLabel.setVisible(false);
      return;
    }

    const cornerPoint = this.activeState.sectionLine.getStart();
    if (!cornerPoint) {
      this.orientationLockedLabel.setVisible(false);
      return;
    }

    this.orientationLockedLabel.setPosition(cornerPoint);
    this.orientationLockedLabel.setVisible(true);
  }

  onClick(mouse: THREE.Vector2, button: THREE.MOUSE) {
    if (button === THREE.MOUSE.RIGHT) {
      if (!this.activeState) return;
      if (this.refiningLastPlane) {
        this.activeState.planeCreator.removeMarker();
        this.onMouseMove(mouse);
        return;
      }
      const stateToRemove = this.states.pop();
      if (stateToRemove) {
        this.removeFromScene(stateToRemove);
        this.activeState.sectionLine.reset();

        const newState = this.states.at(-1);
        if (newState) {
          this.activeState.floorplanPlane = newState.floorplanPlane;
        }

        this.onMouseMove(mouse);
      }
    } else if (button === THREE.MOUSE.LEFT) {
      this.onMouseMove(mouse);
      if (this.switchingPlane || !this.activeState) {
        this.switchFloorplanPlane();
      } else if (this.antiDiscoMode) {
        // Clicking does nothing
      } else if (this.closing) {
        this.close();
      } else if (this.refiningLastPlane) {
        this.activeState.planeCreator.addMarker(mouse);
      } else {
        this.activeState.planeCreator.addMarker(mouse);
        this.addLineSegment();
      }
    }
  }

  switchFloorplanPlane() {
    const newFloorplanPlane = this.hoveredPlane;
    if (newFloorplanPlane) {
      if (this.activeState) {
        this.activeState.floorplanPlane = newFloorplanPlane;
      } else {
        this.setSwitchingPlane(false);
        this.activeState = this.createNewState(newFloorplanPlane);
        this.addToScene(this.activeState);
      }

      const previousPlane = this.states.at(-1)?.plane;
      if (previousPlane) {
        this.addLineSegment();
      }
    } else {
      this.viewer.scene.dispatchEvent({
        type: 'pointorama_floorplan_no_ground_plane_warning',
      });
    }
  }

  addLineSegment() {
    if (!this.activeState) return;

    if (this.states.length === 0) {
      this.activeState.sectionLine.firstPointOnLine = this.activeState.sectionLine.secondPointOnLine;
    }

    if (this.activeState.planeCreator.creationMarkers.length < 2) this.scene.remove(this.activeState.plane);
    this.scene.remove(this.activeState.planeCreator);
    this.states.push(this.activeState);
    const basePlane = this.activeState.floorplanPlane;
    this.activeState = this.createNewState(basePlane);
    this.addToScene(this.activeState);
  }

  checkClosingCondition() {
    if (!this.activeState) return;
    if (this.states.length < 3) return;
    const lastLine = this.states.at(-1)?.sectionLine;
    if (!lastLine) return;

    const firstLine = this.states[0].sectionLine;

    this.closing = !!this.viewer.inputHandler.isHovered(this.endingMarker);

    if (this.closing) {
      // Render the floorplan as closed
      if (this.polygonPreview) return;

      const closingCorner = firstLine.findPointOfIntersectionWithOtherLine(lastLine);
      if (!closingCorner) return;

      firstLine.startingCornerPoint = closingCorner;
      lastLine.endingCornerPoint = closingCorner;
      firstLine.updateGeometry();
      lastLine.updateGeometry();
      for (const line of this.states.map((s) => s.sectionLine)) {
        line.visible = false;
      }
      if (this.polygonPreview) this.viewer.scene.removeMeasurement(this.polygonPreview);
      this.polygonPreview = this.convertToPolygon();
      this.viewer.scene.addMeasurement(this.polygonPreview);
      this.endingMarker.highlight();
    } else {
      if (!this.polygonPreview) return;
      // Render the floorplan as open again
      firstLine.startingCornerPoint = undefined;
      firstLine.updateGeometry();

      for (const line of this.states.map((s) => s.sectionLine)) {
        line.visible = true;
      }

      this.viewer.scene.removeMeasurement(this.polygonPreview);
      this.polygonPreview = null;
      this.endingMarker.unHighlight();
    }
  }

  close() {
    if (!this.activeState) return;
    if (!this.polygonPreview) return;

    // Extract raw polygon points
    const polygonPoints = this.states.map((state) => {
      const pos = state.sectionLine.startingCornerPoint || new THREE.Vector3();
      return { x: pos.x, y: pos.y, z: pos.z };
    });

    // Dispatch polygon points event
    this.viewer.scene.dispatchEvent({
      type: 'floorplan_polygon_finished',
      value: { points: polygonPoints },
    });

    this.reset();
  }

  convertToPolygon() {
    const measure = new AreaAnnotation({
      viewer: this.viewer,
      points: this.states.map((state) => {
        const p = {
          position: state.sectionLine.startingCornerPoint || new THREE.Vector3(),
        };
        return p;
      }),
      visible: true,
      color: DefaultColors.OBJECTS_THREE,
      finished: true,

      selected: false,
    });
    return measure;
  }

  onDoubleClick() {
    if (!this.activeState) return;

    if (this.states.length === 1) {
      const state = this.states[0];
      state.sectionLine.defineFromIntersectingPlanes(state.floorplanPlane, state.plane);
    }

    // Extract polyline points
    const polylinePoints = [
      ...this.states.map((state) => {
        const pos = state.sectionLine.getStart();
        return { x: pos.x, y: pos.y, z: pos.z };
      }),
      (() => {
        const pos = this.states.at(-1)?.sectionLine.getEnd() || new THREE.Vector3();
        return { x: pos.x, y: pos.y, z: pos.z };
      })(),
    ];

    // Dispatch polyline points event
    this.viewer.scene.dispatchEvent({
      type: 'floorplan_polygon_finished',
      value: { points: polylinePoints, isPolyline: true },
    });

    this.reset();
  }

  convertToPolyline() {
    if (!this.states.length) return;

    const points = [
      ...this.states.map((state) => {
        const p = {
          position: state.sectionLine.getStart(),
        };
        return p;
      }),
      { position: this.states.at(-1)?.sectionLine.getEnd() || new THREE.Vector3() },
    ];

    const measure = new DistanceAnnotation({
      viewer: this.viewer,
      points,
      visible: true,
      color: DefaultColors.OBJECTS_THREE,
      finished: true,

      selected: false,
    });
    return measure;
  }

  getTemporaryWallPlanes() {
    if (!this.active) return [];
    return this.states.filter((state) => !state.preExistingPlane && state.planeCreator.creationMarkers.length > 1).map((state) => state.plane.fullCircle);
  }
}
